/**
 * Date formatting utility that uses global settings
 */

// Format patterns
const FORMAT_PATTERNS = {
  'DD/MM/YYYY': { day: '2-digit', month: '2-digit', year: 'numeric', sep: '/' },
  'MM/DD/YYYY': { day: '2-digit', month: '2-digit', year: 'numeric', sep: '/', order: 'mdy' },
  'YYYY-MM-DD': { day: '2-digit', month: '2-digit', year: 'numeric', sep: '-', order: 'ymd' },
  'DD-MM-YYYY': { day: '2-digit', month: '2-digit', year: 'numeric', sep: '-' },
  'MMM DD, YYYY': { day: '2-digit', month: 'short', year: 'numeric', sep: ' ', order: 'mdy-text' },
  'DD MMM YYYY': { day: '2-digit', month: 'short', year: 'numeric', sep: ' ', order: 'dmy-text' },
};

/**
 * Format a date according to the global date format setting
 * @param {string|Date} dateStr - The date to format (ISO string or Date object)
 * @param {string} format - The format pattern (e.g., 'DD/MM/YYYY')
 * @param {string} timezone - The timezone to use (e.g., 'Africa/Johannesburg')
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateStr, format = 'DD/MM/YYYY', timezone = 'UTC') => {
  if (!dateStr) return '-';
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    
    // Convert to target timezone
    const options = { timeZone: timezone };
    const parts = new Intl.DateTimeFormat('en-US', {
      ...options,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).formatToParts(date);
    
    const day = parts.find(p => p.type === 'day')?.value || '';
    const month = parts.find(p => p.type === 'month')?.value || '';
    const year = parts.find(p => p.type === 'year')?.value || '';
    
    // Get month name for text formats
    const monthName = new Intl.DateTimeFormat('en-US', {
      ...options,
      month: 'short',
    }).format(date);
    
    switch (format) {
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'DD-MM-YYYY':
        return `${day}-${month}-${year}`;
      case 'MMM DD, YYYY':
        return `${monthName} ${day}, ${year}`;
      case 'DD MMM YYYY':
        return `${day} ${monthName} ${year}`;
      default:
        return `${day}/${month}/${year}`;
    }
  } catch (error) {
    console.error('Date format error:', error);
    return '-';
  }
};

/**
 * Format a date with time according to global settings
 * @param {string|Date} dateStr - The date to format
 * @param {string} format - The date format pattern
 * @param {string} timezone - The timezone to use
 * @returns {string} - Formatted date and time string
 */
export const formatDateTime = (dateStr, format = 'DD/MM/YYYY', timezone = 'UTC') => {
  if (!dateStr) return '-';
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    
    const dateFormatted = formatDate(dateStr, format, timezone);
    
    // Format time in the target timezone
    const timeStr = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date);
    
    return `${dateFormatted} at ${timeStr}`;
  } catch (error) {
    console.error('DateTime format error:', error);
    return '-';
  }
};

/**
 * Get a relative time description (e.g., "2 days ago", "in 3 hours")
 * @param {string|Date} dateStr - The date to compare
 * @returns {string} - Relative time string
 */
export const getRelativeTime = (dateStr) => {
  if (!dateStr) return '-';
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    
    const now = new Date();
    const diffMs = date - now;
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.round(diffMs / (1000 * 60));
    
    if (Math.abs(diffDays) >= 1) {
      if (diffDays === -1) return 'Yesterday';
      if (diffDays === 1) return 'Tomorrow';
      if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
      return `In ${diffDays} days`;
    }
    
    if (Math.abs(diffHours) >= 1) {
      if (diffHours < 0) return `${Math.abs(diffHours)} hours ago`;
      return `In ${diffHours} hours`;
    }
    
    if (diffMinutes < 0) return `${Math.abs(diffMinutes)} minutes ago`;
    if (diffMinutes === 0) return 'Just now';
    return `In ${diffMinutes} minutes`;
  } catch (error) {
    return '-';
  }
};

export default formatDate;
